import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  Card,
  CardMedia,
  Divider,
  Grid,
  Avatar,
  useTheme,
  useMediaQuery, Skeleton,
} from '@mui/material';
import {
  CalendarToday,
  Person,
  Visibility,
  ArrowBack,
  Share,
  BookmarkBorder,
  ThumbUp
} from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { listBlogs } from '../../graphql/queries';
import { useParams, useNavigate } from 'react-router-dom';
import Seo from '../../components/Common/Seo';
import { useSelector } from 'react-redux';
import { surface, brand, ink, line } from '../../theme/tokens';

const client = generateClient();

// Tag colours are author-supplied, so a solid fill can land anywhere on the
// luminance range -- olive at 3.51:1 and clay at 3.82:1 both fail AA whichever
// ink you pick. Render the swatch as a 16% tint plus a full-strength border
// instead: the tag colour still reads, and the label stays on ink.primary,
// which is >=7.5:1 against any tint of any colour over the card.
const tagChipStyle = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) {
    return { backgroundColor: surface.sunken, borderColor: line.default, color: ink.primary };
  }
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.16)`,
    borderColor: `rgba(${r}, ${g}, ${b}, 0.85)`,
    color: ink.primary,
  };
};

const captionBarSx = {
  px: { xs: 1.5, md: 2 },
  py: { xs: 1, md: 1.25 },
  textAlign: 'center',
  bgcolor: surface.sunken,
  borderTop: `1px solid ${line.subtle}`,
};

const sectionTitleSx = {
  color: ink.primary,
  fontWeight: 700,
  letterSpacing: '-0.01em',
};

// Query to get blog with sections and media
const getBlogWithSections = /* GraphQL */ `
  query GetBlogWithSections($id: ID!) {
    getBlog(id: $id) {
      id
      title
      slug
      description
      content
      thumbnailImage
      thumbnailImageAlt
      tags {
        items {
          id
          blogId
          blogTagId
          blogTag {
            id
            name
            color
            __typename
          }
          __typename
        }
        nextToken
        __typename
      }
      status
      isFeatured
      featuredOrder
      viewCount
      estimatedReadTime
      metaTitle
      metaDescription
      authorId
      author {
        id
        email
        name
        __typename
      }
      sections {
        items {
          id
          blogId
          type
          order
          title
          content
          settings
          media {
            items {
              id
              sectionId
              type
              originalUrl
              thumbnailUrl
              altText
              caption
              order
              width
              height
              fileSize
              duration
              __typename
            }
            nextToken
            __typename
          }
          createdAt
          updatedAt
          __typename
        }
        nextToken
        __typename
      }
      publishedAt
      createdAt
      updatedAt
      __typename
    }
  }
`;

const BlogDetailPage = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processedSections, setProcessedSections] = useState([]);
  
  // Get user authentication status and role
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      
      // Get blog by ID
      const response = await client.graphql({
        query: getBlogWithSections,
        variables: { id: id }
      });
      
      const blogData = response.data.getBlog;
      console.log('Found blog data:', blogData);
      console.log('Blog status:', blogData?.status);
      console.log('Blog sections:', blogData?.sections?.items?.length || 0);
      if (!blogData) {
        setError('Blog not found');
        return;
      }
      
      // Check if blog is published or if user is admin
      if (blogData.status !== 'PUBLISHED' && !isAdmin) {
        console.log('Blog not published, status:', blogData.status);
        setError(`This blog is currently in ${blogData.status.toLowerCase()} status and not available for public viewing.`);
        return;
      }
      
      console.log('Access granted - Status:', blogData.status, 'IsAdmin:', isAdmin);
      
      // Process thumbnail image
      let thumbnailUrl = '';
      if (blogData.thumbnailImage) {
        try {
          if (blogData.thumbnailImage.startsWith('http')) {
            thumbnailUrl = blogData.thumbnailImage;
          } else {
            const url = await getUrl({ key: blogData.thumbnailImage });
            thumbnailUrl = url.url.toString();
          }
        } catch (error) {
          console.error('Error getting thumbnail URL:', error);
        }
      }
      
      // Process sections and their media
      const sections = blogData.sections?.items || [];
      console.log('Blog sections found:', sections.length, sections);
      const processedSections = await Promise.all(
        sections
          .sort((a, b) => a.order - b.order)
          .map(async (section) => {
            const media = section.media?.items || [];
            const processedMedia = await Promise.all(
              media.map(async (mediaItem) => {
                let originalUrl = '';
                let thumbnailUrl = '';
                
                try {
                  if (mediaItem.originalUrl) {
                    if (mediaItem.originalUrl.startsWith('http')) {
                      originalUrl = mediaItem.originalUrl;
                    } else {
                      const url = await getUrl({ key: mediaItem.originalUrl });
                      originalUrl = url.url.toString();
                    }
                  }
                  
                  if (mediaItem.thumbnailUrl) {
                    if (mediaItem.thumbnailUrl.startsWith('http')) {
                      thumbnailUrl = mediaItem.thumbnailUrl;
                    } else {
                      const url = await getUrl({ key: mediaItem.thumbnailUrl });
                      thumbnailUrl = url.url.toString();
                    }
                  }
                } catch (error) {
                  console.error('Error getting media URLs:', error);
                }
                
                return {
                  ...mediaItem,
                  originalDisplayUrl: originalUrl,
                  thumbnailDisplayUrl: thumbnailUrl
                };
              })
            );
            
            return {
              ...section,
              processedMedia
            };
          })
      );
      
      console.log('Setting blog data with thumbnail URL:', thumbnailUrl);
      console.log('Processed sections count:', processedSections.length);
      
      setBlog({
        ...blogData,
        thumbnailDisplayUrl: thumbnailUrl
      });
      setProcessedSections(processedSections);
      
      console.log('Blog state updated successfully');
      
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load blog. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderSection = (section) => {
    switch (section.type) {
      case 'TEXT':
        return (
          <Box sx={{ mb: { xs: 2, md: 4 } }}>
            {section.title && (
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                gutterBottom
                sx={{ ...sectionTitleSx, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }, mb: 2 }}
              >
                {section.title}
              </Typography>
            )}
            <Typography
              variant="body1"
              sx={{ 
                color: ink.primary,
                lineHeight: 1.8,
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
                '& p': { mb: { xs: 1.5, md: 2 } },
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 8 }
              }}
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          </Box>
        );
      
      case 'IMAGE':
        const image = section.processedMedia?.[0];
        if (!image) return null;
        
        return (
          <Box sx={{ mb: { xs: 2, md: 4 } }}>
            {section.title && (
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ ...sectionTitleSx, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 1.5 }}
              >
                {section.title}
              </Typography>
            )}
            <Card sx={{ borderRadius: { xs: 1, md: 2 } }}>
              <CardMedia
                component="img"
                image={image.originalDisplayUrl}
                alt={image.altText || section.title}
                sx={{ 
                  maxHeight: { xs: 300, sm: 400, md: 600 }, 
                  objectFit: 'cover',
                  width: '100%'
                }}
              />
              {image.caption && (
                <Box sx={captionBarSx}>
                  <Typography variant="caption" sx={{ color: ink.secondary }}>
                    {image.caption}
                  </Typography>
                </Box>
              )}
            </Card>
          </Box>
        );
      
      case 'VIDEO':
        const video = section.processedMedia?.[0];
        if (!video) return null;
        
        return (
          <Box sx={{ mb: { xs: 2, md: 4 } }}>
            {section.title && (
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ ...sectionTitleSx, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 1.5 }}
              >
                {section.title}
              </Typography>
            )}
            <Card sx={{ borderRadius: { xs: 1, md: 2 } }}>
              <video
                controls
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  maxHeight: isMobile ? '250px' : '400px'
                }}
                src={video.originalDisplayUrl}
                poster={video.thumbnailDisplayUrl}
                playsInline
              />
              {video.caption && (
                <Box sx={captionBarSx}>
                  <Typography variant="caption" sx={{ color: ink.secondary }}>
                    {video.caption}
                  </Typography>
                </Box>
              )}
            </Card>
          </Box>
        );
      
      case 'IMAGE_GALLERY':
        if (!section.processedMedia?.length) return null;
        
        return (
          <Box sx={{ mb: { xs: 2, md: 4 } }}>
            {section.title && (
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                gutterBottom
                sx={{ ...sectionTitleSx, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 1.5 }}
              >
                {section.title}
              </Typography>
            )}
            <Grid container spacing={{ xs: 1, md: 2 }}>
              {section.processedMedia
                .sort((a, b) => a.order - b.order)
                .map((image, index) => (
                  <Grid item xs={6} sm={6} md={4} key={index}>
                    <Card sx={{ borderRadius: { xs: 1, md: 2 } }}>
                      <CardMedia
                        component="img"
                        height={isMobile ? "180" : "250"}
                        image={image.thumbnailDisplayUrl || image.originalDisplayUrl}
                        alt={image.altText}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: { xs: 'none', md: 'scale(1.02)' }
                          }
                        }}
                        onClick={() => window.open(image.originalDisplayUrl, '_blank')}
                      />
                      {image.caption && (
                        <Box sx={captionBarSx}>
                          <Typography
                            variant="caption"
                            sx={{ color: ink.secondary, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                          >
                            {image.caption}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
            </Grid>
          </Box>
        );
      
      case 'QUOTE':
        const author = section.settings?.author;
        return (
          <Box sx={{ 
            mb: { xs: 2, md: 4 }, 
            py: { xs: 2, md: 3 }, 
            px: { xs: 2, md: 4 }, 
            bgcolor: surface.sunken, 
            borderLeft: 4, 
            borderColor: brand.mustard,
            borderRadius: { xs: 1, md: 2 }
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: ink.primary,
                fontStyle: 'italic', 
                fontWeight: 500,
                lineHeight: 1.6,
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
              }}
            >
              "{section.content}"
            </Typography>
            {author && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: ink.secondary,
                  textAlign: 'right',
                  fontSize: { xs: '0.8rem', md: '0.875rem' }
                }}
              >
                — {author}
              </Typography>
            )}
          </Box>
        );
      
      case 'DIVIDER':
        return (
          <Box sx={{ my: 5 }}>
            <Divider sx={{ borderColor: line.default }} />
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Skeleton height={52} sx={{ mb: 1 }} />
        <Skeleton height={52} width="70%" sx={{ mb: 2 }} />
        <Skeleton height={20} width="35%" sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2, mb: 4 }} />
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Skeleton key={i} height={22} width={i % 4 === 3 ? '60%' : '100%'} />
        ))}
      </Container>
    );
  }

  if (error || !blog) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Blog not found'}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/blog')}
          sx={{ mt: 2 }}
        >
          Back to Blog List
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Seo
        title={blog.metaTitle || blog.title}
        description={blog.metaDescription || blog.description}
        path={`/blog/${id}/${slug}`}
        type="article"
        image={blog.thumbnailDisplayUrl || undefined}
      />
      
      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 }
        }}
      >
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/blog')}
          sx={{ 
            mb: { xs: 3, md: 4 },
            px: 0,
            color: ink.secondary,
            fontWeight: 600,
            fontSize: { xs: '0.875rem', md: '1rem' },
            minHeight: { xs: 40, md: 36 },
            '&:hover': { backgroundColor: 'transparent', color: ink.primary },
          }}
        >
          Back to Blog
        </Button>

        {/* Blog Header */}
        <Box sx={{ mb: 4 }}>
          {/* Draft Status Indicator for Admins */}
          {blog.status !== 'PUBLISHED' && isAdmin && (
            <Chip
              label={`${blog.status} - Admin Preview`}
              color="warning"
              sx={{ mb: 2 }}
            />
          )}
          
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            component="h1" 
            gutterBottom
            sx={{ 
              color: ink.primary,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.15 },
              // 400 at display size read as a caption, not a headline
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}
          >
            {blog.title}
          </Typography>
          
          {blog.description && (
            <Typography 
              variant={isMobile ? "body1" : "h6"} 
              paragraph
              sx={{ 
                color: ink.secondary,
                fontWeight: 400,
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                lineHeight: 1.6,
                mb: { xs: 2, md: 3 }
              }}
            >
              {blog.description}
            </Typography>
          )}
          
          {/* Blog Meta */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 2, md: 3 }, 
            mb: { xs: 3, md: 4 }, 
            py: 2,
            borderTop: `1px solid ${line.subtle}`,
            borderBottom: `1px solid ${line.subtle}`,
            flexWrap: 'wrap',
            fontSize: { xs: '0.875rem', md: '1rem' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: { xs: 28, md: 32 },
                  height: { xs: 28, md: 32 },
                  bgcolor: brand.brown,
                  color: ink.onDark,
                }}
              >
                <Person sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />
              </Avatar>
              <Typography
                variant="body2"
                sx={{ color: ink.primary, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}
              >
                {blog.author?.name || 'Farm Team'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 18, color: ink.muted }} />
              <Typography variant="body2" sx={{ color: ink.muted }}>
                {formatDate(blog.publishedAt || blog.createdAt)}
              </Typography>
            </Box>
            
            {blog.estimatedReadTime && (
              <Typography variant="body2" sx={{ color: ink.muted }}>
                {blog.estimatedReadTime} min read
              </Typography>
            )}
            
            {blog.viewCount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Visibility sx={{ fontSize: 18, color: ink.muted }} />
                <Typography variant="body2" sx={{ color: ink.muted }}>
                  {blog.viewCount} views
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Tags */}
          {blog.tags?.items?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {blog.tags.items.map((tagConnection) => (
                <Chip
                  key={tagConnection.blogTag.id}
                  label={tagConnection.blogTag.name}
                  size="small"
                  sx={{ mr: 1, mb: 1, fontWeight: 600, borderWidth: 1, borderStyle: 'solid' }}
                  style={tagChipStyle(tagConnection.blogTag.color)}
                />
              ))}
            </Box>
          )}
          
          {/* Featured Image */}
          {blog.thumbnailDisplayUrl && (
            <Card sx={{ mb: 4, border: `1px solid ${line.subtle}` }}>
              <CardMedia
                component="img"
                image={blog.thumbnailDisplayUrl}
                alt={blog.thumbnailImageAlt || blog.title}
                sx={{ 
                  maxHeight: { xs: 250, sm: 350, md: 400 }, 
                  objectFit: 'cover',
                  width: '100%'
                }}
              />
            </Card>
          )}
        </Box>

        {/* Main Content (content field) */}
        {blog.content && (
          <Box sx={{ 
            mb: 5,
            color: ink.primary,
            '& h1': { fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, mt: 5, mb: 2, color: ink.primary, letterSpacing: '-0.01em' },
            '& h2': { fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 700, mt: 4, mb: 1.5, color: ink.primary, letterSpacing: '-0.01em' },
            '& h3': { fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 600, mt: 3, mb: 1, color: ink.primary },
            // body copy sits at ink.primary (9.48:1); the muted ramp is for meta
            '& p': { fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.8, mb: 2.5, color: ink.primary },
            '& ul, & ol': { pl: 3, mb: 2.5 },
            '& li': { fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.8, mb: 0.75, color: ink.primary },
            '& a': { color: brand.mustardInk, fontWeight: 600 },
            '& blockquote': { 
              borderLeft: 4, borderColor: brand.mustard, pl: 2.5, py: 1.5, my: 3, mx: 0,
              bgcolor: surface.sunken, borderRadius: 1, fontStyle: 'italic', color: ink.primary
            },
            '& strong': { fontWeight: 700, color: ink.primary },
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2 },
            '& hr': { my: 4, borderColor: line.default },
          }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {blog.content}
            </ReactMarkdown>
          </Box>
        )}

        {/* Blog Sections */}
        <Box sx={{ mb: 6 }}>
          {processedSections.map((section) => (
            <Box key={section.id}>
              {renderSection(section)}
            </Box>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, md: 2 }, 
          justifyContent: 'center', 
          borderTop: `1px solid ${line.default}`, 
          pt: { xs: 3, md: 4 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          '& .MuiButton-root': { color: ink.secondary, fontWeight: 600 },
          '& .MuiButton-root:hover': { color: ink.primary, backgroundColor: line.subtle },
        }}>
          <Button
            startIcon={<Share />}
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: blog.title,
                  text: blog.description,
                  url: window.location.href
                });
              } else {
                navigator.clipboard.writeText(window.location.href);
              }
            }}
            sx={{ 
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              minWidth: { xs: 80, md: 'auto' },
              px: { xs: 1, md: 2 }
            }}
          >
            {isSmallMobile ? 'Share' : 'Share'}
          </Button>
          
          <Button 
            startIcon={<BookmarkBorder />}
            sx={{ 
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              minWidth: { xs: 80, md: 'auto' },
              px: { xs: 1, md: 2 }
            }}
          >
            {isSmallMobile ? 'Save' : 'Save'}
          </Button>
          
          <Button 
            startIcon={<ThumbUp />}
            sx={{ 
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              minWidth: { xs: 80, md: 'auto' },
              px: { xs: 1, md: 2 }
            }}
          >
            {isSmallMobile ? 'Like' : 'Like'}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default BlogDetailPage;